import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Button from 'src/components/atoms/Button';
import TableImage from 'src/components/atoms/TableImage';
import ItemsGrid from 'src/components/layouts/ItemsGrid';
import MultipleViews from 'src/components/layouts/MultipleViews';
import Page from 'src/components/layouts/Page';
import EmptyPage from 'src/components/molecules/EmptyPage';
import TableFilterable from 'src/components/organisms/TableFilterable';
import css from './moveme.module.scss';

import SearchBar from 'src/components/organisms/SearchBar';
import { getCounts } from 'src/slices/info';
import {
  bulkDelete,
  bulkHide,
  clearState,
  deleteItem,
  getItems,
  setView,
  updateFilter,
  updateItem,
} from 'src/slices/item';
import { checkPermissions, handleStatus } from 'src/slices/utils';
// import { useNavigate } from 'react-router-dom';
import { useDebouncer } from 'src/utils/debounce';

// we're using React.useMemo here to ensure that our data isn't recreated on every render.
// accessor is the "key" in the data

export default function Items(params) {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const { permissions } = useSelector((state) => state.auth);
  const { items, status, itemsView, displayShowMore } = useSelector((state) => state.item);
  const { status: empStatus } = useSelector((state) => state.employee);

  const filter = useSelector((state) => state.item.filters.items);
  // const { counts } = useSelector((state) => state.info);

  function handleDelete(message, deleteFunction) {
    const ConfirmActionModal = ({ closeToast, toastProps }) => (
      <div>
        Are you sure you want to delete this offer? This action cannot be undone.
        <div className={css.buttons}>
          <Button color="green" action={() => deleteFunction()} label={message}></Button>
          <Button color="red" label={'Dismiss'}></Button>
        </div>
      </div>
    );
    toast(ConfirmActionModal, { autoClose: false });
  }

  const bulkActions = [
    {
      value: (items) =>
        handleDelete(`Delete ${items.length} items`, () => dispatch(bulkDelete(items))),
      label: 'Bulk Delete',
    },
    {
      value: (items) => dispatch(bulkHide({ type: 'hide', items })),
      label: 'Bulk Hide',
    },
    {
      value: (items) => dispatch(bulkHide({ type: 'unhide', items })),
      label: 'Bulk Unhide',
    },
  ];

  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    dispatch(getCounts());
  }, []);

  useEffect(() => {
    dispatch(updateFilter({ items: { keyword: '', page: 1, skip: 200, noHidden: false } }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getItems());
  }, [filter, dispatch]);

  useEffect(() => {
    handleStatus(status.deleteItem, () => dispatch(clearState(status.deleteItem)));
    handleStatus(status.updateItem, () => dispatch(clearState(status.updateItem)), {});
  }, [status]);

  useEffect(() => {
    handleStatus(empStatus.approveResource, () => dispatch(clearState(empStatus.approveResource)));
  }, [empStatus.approveResource]);

  const handleMore = () => {
    dispatch(
      updateFilter({ items: { limit: (page + 1) * filter.skip, page: page + 1, noHidden: false } }),
    );
    setPage(page + 1);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Image',
        accessor: 'photos',
        disableSortBy: true,
        width: '80px',
        Cell: (props) => (
          <TableImage
            iconUnicode=""
            src={props.cell.row.values.photos?.[0]}
            type="rect"
            alt={props.cell.row.values.name}
          />
        ),
      },
      {
        Header: 'Name',
        accessor: 'name',
        width: 'auto',
      },
      {
        Header: 'Price',
        accessor: 'price',
        Cell: (props) => `$${props.cell.row.values.price.toFixed(2)}`,
        width: 'auto',
      },
      {
        Header: 'Quantity',
        accessor: 'qty',
        Cell: (props) => `${props.cell.row.values.qty.value} ${props.cell.row.values.qty.units}`,
        width: 'auto',
      },
      // {
      //   Header: 'Category',
      //   accessor: 'category',
      //   width: 'auto',
      //   Cell: (props) => (
      //     <Pill
      //       label={props.cell.row.values.category || 'Other'}
      //       color="normal"
      //       variant="secondary"
      //     />
      //   ),
      // },
      {
        // Header: "Actions",
        Header: ' ',
        accessor: 'id',
        disableSortBy: true,
        Cell: (props) => (
          <div className={css['table-actions']}>
            {checkPermissions(permissions, { update: ['items'] }).hasPermission && (
              <>
                <Button
                  iconLeft="fad fa-pencil"
                  color="gray"
                  variant="secondary"
                  href={`/items/${props.cell.row.values.id}`}
                  size="s"
                />

                <Button
                  iconLeft={
                    props.cell.row.original.status == 'active' ? 'fas fa-eye' : 'fas fa-eye-slash'
                  }
                  color="gray"
                  variant="secondary"
                  size="s"
                  action={() =>
                    dispatch(
                      updateItem({
                        id: props.cell.row.values.id,
                        status: props.cell.row.original.status == 'active' ? 'hidden' : 'active',
                      }),
                    )
                  }
                />
              </>
            )}
            {checkPermissions(permissions, { delete: ['items'] }).hasPermission && (
              <Button
                iconLeft="fad fa-trash-alt"
                color="red"
                variant="secondary"
                size="s"
                action={() =>
                  handleDelete(`Delete '${props.cell.row.original.name}'`, () =>
                    dispatch(deleteItem(props.cell.row.original.id)),
                  )
                }
              />
            )}
          </div>
        ),
      },
      ,
    ],

    [],
  );

  const handleSearch = (keyword) => {
    dispatch(updateFilter({ items: { keyword: keyword, page: 1, noHidden: false } }));
    setPage(1);
  };

  const debouncedSearch = useDebouncer(handleSearch);

  return (
    <Page>
      <MultipleViews
        // TODO: Card view default for mobile
        showControls
        defaultView="grid" // grid = card view
        isEmpty={!status.getItems.isLoading && items.length === 0}
        setView={(view) => dispatch(setView({ items: view }))}
        view={itemsView}
        isLocked={!permissions.read.includes('items')}
        searchBar={
          <SearchBar
            onChange={(e) => debouncedSearch(e.target.value)}
            value={filter.keyword}
            dataName="item"
            options={bulkActions}
            createLink="/items/new"
            view={itemsView}
            selected={selected}
          />
        }
        tableView={
          status.getItems.isLoading && page <= 1 ? (
            <EmptyPage title="Loading..." iconName="fad fa-spinner fa-spin" />
          ) : (
            <TableFilterable
              columns={columns}
              data={items}
              cellPadding="8px"
              dataName="item"
              showBar={false}
              setSelected={(selected) => setSelected(selected)}
              handleMore={handleMore}
              displayShowMore={displayShowMore}
              loadingMore={status.getItems.isLoading && page > 1}
            />
          )
        }
        gridView={
          status.getItems.isLoading && page <= 1 ? (
            <EmptyPage title="Loading..." iconName="fad fa-spinner fa-spin" />
          ) : (
            <ItemsGrid
              items={items}
              displayShowMore={displayShowMore}
              handleMore={handleMore}
              loadingMore={status.getItems.isLoading && page > 1}
            />
          )
        }
        emptyView={
          !status.getItems.isLoading &&
          (!!filter.keyword && items.length == 0 ? (
            <EmptyPage
              title="No items found"
              text={
                <>
                  There are no items matching '{filter.keyword}'.
                  <br />
                  Try something else for better reseults.
                </>
              }
              iconName="fad fa-inventory"
            />
          ) : (
            <EmptyPage
              title="Search to locate a product from your inventory"
              iconName="fad fa-search"
            />
          ))
        }
        lockedView={
          <EmptyPage
            title="Access Denied"
            text="You don’t have permission to view anything on this page. Ask your employer for access"
            iconName="fad fa-lock"
          />
        }
      />
    </Page>
  );
}
